// services/v3/config.ts — effective V3 runtime configuration (SPEC §2)
import { useRuntimeConfig } from '#imports'

export type DataSourceKind = 'rtdb' | 'firestore' | 'mock'
export type WriteMode = 'readonly' | 'live'

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  databaseURL: string
  projectId: string
  storageBucket: string
}

export interface V3Config {
  dataSource: DataSourceKind
  writeMode: WriteMode
  rtdbTtlMs: number
  v3Entry: boolean
  firebase: FirebaseConfig
  sentryEnv: string
  storeApiBase: string
}

const KINDS: DataSourceKind[] = ['rtdb', 'firestore', 'mock']

const asString = (v: unknown, fallback = ''): string =>
  typeof v === 'string' ? v : (v === undefined || v === null ? fallback : String(v))

const asBool = (v: unknown): boolean =>
  v === true || v === 'true' || v === 1 || v === '1'

const asNumber = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : parseInt(asString(v), 10)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

/**
 * Reads `runtimeConfig.public` and resolves the effective values.
 * `writeMode` defaults to 'live' in a production build, 'readonly' otherwise.
 */
export function readV3Config(): V3Config {
  const pub = useRuntimeConfig().public as Record<string, unknown>
  const xtable = (pub.xtable ?? {}) as Record<string, unknown>
  const firebase = (pub.firebase ?? {}) as Record<string, unknown>
  const sentry = (pub.sentry ?? {}) as Record<string, unknown>

  const kind = asString(xtable.dataSource, 'rtdb') as DataSourceKind
  const configuredWriteMode = asString(xtable.writeMode).trim()
  const writeMode: WriteMode = configuredWriteMode === 'live' || configuredWriteMode === 'readonly'
    ? configuredWriteMode
    : (process.env.NODE_ENV === 'production' ? 'live' : 'readonly')

  return {
    dataSource: KINDS.includes(kind) ? kind : 'rtdb',
    writeMode,
    rtdbTtlMs: asNumber(xtable.rtdbTtlMs, 600000),
    v3Entry: asBool(xtable.v3Entry),
    firebase: {
      apiKey: asString(firebase.apiKey),
      authDomain: asString(firebase.authDomain),
      databaseURL: asString(firebase.databaseURL),
      projectId: asString(firebase.projectId),
      storageBucket: asString(firebase.storageBucket),
    },
    sentryEnv: asString(sentry.environment),
    storeApiBase: asString(pub.storeApiBase),
  }
}

// ---------------------------------------------------------------------------
// Cloudflare Images (see nuxt3-pos-v3 config.ts — same account, same variants)
// ---------------------------------------------------------------------------

/**
 * Variant names as configured on Cloudflare Images > Variants.
 * Cloudflare resizes by variant name, we only append it to the delivery URL.
 * App code must never write these strings inline — go through CF_IMAGE_VARIANT.
 *
 * The name IS the width in px (every variant is square). Pick one from the real
 * painted size x DPR, not from taste: a 64px thumbnail on a 2x screen needs 240,
 * asking for 1080 and scaling it down burns the guest's data for nothing.
 *
 * *** The right-hand side must exist on Cloudflare or the URL 404s. ***
 * The account had 240, 360, 480, 720, 1080 when last checked (2026-09-17).
 */
export const CF_IMAGE_VARIANT = {
  /** list rows / menu buttons — the workhorse */
  '240': '240',
  '360': '360',
  '480': '480',
  /** full-width header image inside a modal on a phone */
  '720': '720',
  '1080': '1080',
} as const

export type CfImageVariant = keyof typeof CF_IMAGE_VARIANT

/**
 * Used when the caller does not say. Deliberately the smallest one —
 * forgetting the variant and getting a small image beats loading 1080 everywhere.
 */
export const CF_IMAGE_VARIANT_DEFAULT: CfImageVariant = '240'

/**
 * Base of the Cloudflare delivery URL (`https://imagedelivery.net/<account-hash>`).
 * Empty = no URL can be built, so images fall back to the S3 `image.url`.
 *
 * Wrapped in try/catch because callers outside a Nuxt context (a plain service
 * module imported at boot) have no runtimeConfig yet — this must never throw.
 */
export function readCfDeliveryUrl(): string {
  try {
    const cf = useRuntimeConfig().public.cloudflareImages as { deliveryUrl?: string } | undefined
    return (cf?.deliveryUrl || '').replace(/\/+$/, '')
  } catch {
    return ''
  }
}

/**
 * Base of the Cloudflare R2 public domain (`https://xtable-cdn.co`) that serves
 * the new menu images (key = `assets/<storeId>/products/..`, stored on `cfIdR2`).
 * Empty = no URL can be built, so images fall back to the next candidate / S3.
 *
 * Wrapped in try/catch for the same reason as readCfDeliveryUrl (callers outside
 * a Nuxt context have no runtimeConfig). See composables/v3/useR2ImagePath.ts.
 */
export function readR2CdnUrl(): string {
  try {
    const r2 = useRuntimeConfig().public.r2Public as { cdnUrl?: string } | undefined
    return (r2?.cdnUrl || '').replace(/\/+$/, '')
  } catch {
    return ''
  }
}
